import {
  AssistantProcess,
  PatchAssistantProcessBody,
  validateAssistantProcess,
} from "@models";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useDebounceQueryMutation } from "src/hooks/useDebounceMutation";
import { getAssistantProcessesQueryKey } from "../../config";
import { patchAssistantProcess, postAssistantProcess } from "../../services";

export const usePostAssistantProcessMutation = () => {
  return useMutation({
    mutationFn: async ({
      data,
      orgId,
      deviceId,
    }: {
      data: { state: AssistantProcess["state"] };
      orgId: string;
      deviceId: string;
    }) => {
      const response = await postAssistantProcess({
        data,
        orgId,
        deviceId,
      });

      return response;
    },
  });
};

export const useUpdateAssistantProcessMutation = (
  orgId: string,
  deviceId: string
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      processId,
      body,
    }: {
      processId: string;
      body: PatchAssistantProcessBody;
    }) => {
      const { data } = await patchAssistantProcess(
        orgId,
        deviceId,
        processId,
        body
      );
      return validateAssistantProcess(data);
    },
    onMutate: async ({ processId, body }) => {
      // Cancel any outgoing refetches so they don't overwrite our optimistic update
      await queryClient.cancelQueries({
        queryKey: getAssistantProcessesQueryKey(orgId, deviceId),
      });

      // Snapshot the previous value
      const previousData: AssistantProcess[] | undefined =
        queryClient.getQueryData(
          getAssistantProcessesQueryKey(orgId, deviceId)
        );

      queryClient.setQueryData(
        getAssistantProcessesQueryKey(orgId, deviceId),
        (old: AssistantProcess[] | undefined) => {
          return old?.map((process) =>
            process.id === processId ? { ...process, ...body } : process
          );
        }
      );

      return { previousData };
    },
    onError: (_, _1, context) => {
      // If the mutation fails, use the context returned from onMutate to roll back
      if (context?.previousData) {
        queryClient.setQueryData(
          getAssistantProcessesQueryKey(orgId, deviceId),
          context.previousData
        );
      }
    },
  });
};

export const useSimpleUpdateAssistantProcessMutation = (
  orgId: string,
  deviceId: string
) => {
  return useMutation({
    mutationFn: async ({
      processId,
      body,
    }: {
      processId: string;
      body: PatchAssistantProcessBody;
    }) => {
      const { data } = await patchAssistantProcess(
        orgId,
        deviceId,
        processId,
        body
      );
      return validateAssistantProcess(data);
    },
  });
};

export const useUpdateAssistantProcessMutationDebounced = (
  orgId: string,
  deviceId: string
) => {
  const mutation = useSimpleUpdateAssistantProcessMutation(orgId, deviceId);

  return useDebounceQueryMutation<
    { processId: string; body: PatchAssistantProcessBody },
    AssistantProcess[],
    AssistantProcess
  >(mutation.mutateAsync, {
    delay: 1000,
    optimisticUpdate: {
      queryKey: getAssistantProcessesQueryKey(orgId, deviceId),
      updateFn: (oldData, variables) => {
        return oldData?.map((process) =>
          process.id === variables.processId
            ? { ...process, ...variables.body }
            : process
        );
      },
    },
  });
};
